import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

export class RenderService {
  constructor() {
    this.ffmpeg = new FFmpeg();
    this.ffmpegLoaded = false;
    this.cancelRender = false;
  }

  async init() {
    if (this.ffmpegLoaded) return;

    try {
      await this.ffmpeg.load({
        coreURL:
          "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.js",
        wasmURL:
          "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.wasm",
      });
      this.ffmpegLoaded = true;
    } catch (error) {
      console.error("Error initializing FFmpeg:", error);
      throw new Error("Failed to initialize renderer");
    }
  }

  async renderVideo(
    images,
    settings,
    onProgress,
    rangeStartDate,
    rangeEndDate
  ) {
    if (!this.ffmpegLoaded) {
      await this.init();
    }

    const { fps = 24, width = 1920, height = 1080, quality = 0.8 } = settings;

    try {
      // Filter images based on the selected time range
      const startTime = rangeStartDate.getTime();
      const endTime = rangeEndDate.getTime();

      const rangeImages = images.filter((img) => {
        const timestamp = img.userTimestamp || img.timestamp;
        return timestamp >= startTime && timestamp <= endTime;
      });

      // Sort images by timestamp to ensure correct order
      rangeImages.sort((a, b) => {
        const timestampA = a.userTimestamp || a.timestamp;
        const timestampB = b.userTimestamp || b.timestamp;
        return timestampA - timestampB; // Oldest to newest for natural timelapse
      });

      if (rangeImages.length === 0) {
        throw new Error("No images found in the selected time range");
      }

      // Reset progress
      onProgress({
        current: 0,
        total: rangeImages.length,
        status: "Processing images...",
      });

      // Process images
      for (let i = 0; i < rangeImages.length; i++) {
        if (this.cancelRender) {
          throw new Error("Render cancelled by user");
        }

        const image = rangeImages[i];
        try {
          const response = await fetch(image.sourceUrl);
          const imageBlob = await response.blob();
          const imageArrayBuffer = await imageBlob.arrayBuffer();
          const imageUint8Array = new Uint8Array(imageArrayBuffer);

          const filename = `image${String(i).padStart(5, "0")}.jpg`;
          await this.ffmpeg.writeFile(filename, imageUint8Array);

          onProgress({
            current: i + 1,
            total: rangeImages.length,
            status: `Processing image ${i + 1}/${rangeImages.length}`,
          });
        } catch (error) {
          throw new Error(`Failed to process image ${i + 1}`);
        }
      }

      // Check for cancellation before video generation
      if (this.cancelRender) {
        throw new Error("Render cancelled by user");
      }

      onProgress({
        current: rangeImages.length,
        total: rangeImages.length,
        status: "Generating video...",
      });

      // Calculate CRF value based on quality (0.1-1.0)
      const crf = Math.round(28 - quality * 10);

      // Generate video using FFmpeg
      await this.ffmpeg.exec([
        "-framerate",
        fps.toString(),
        "-i",
        "image%05d.jpg",
        "-c:v",
        "libx264",
        "-preset",
        "veryfast",
        "-profile:v",
        "high",
        "-crf",
        crf.toString(),
        "-pix_fmt",
        "yuv420p",
        "-vf",
        `scale=${width}:${height}:force_original_aspect_ratio=decrease,pad=${width}:${height}:(ow-iw)/2:(oh-ih)/2`,
        "-x264opts",
        "keyint=50:min-keyint=25:scenecut=-1",
        "-movflags",
        "+faststart",
        "-y",
        "output.mp4",
      ]);

      // Read the output file
      const data = await this.ffmpeg.readFile("output.mp4");
      const videoBlob = new Blob([data.buffer], { type: "video/mp4" });
      const videoUrl = URL.createObjectURL(videoBlob);

      // Clean up files
      const files = await this.ffmpeg.listDir(".");
      for (const file of files) {
        if (file.name.startsWith("image") || file.name === "output.mp4") {
          await this.ffmpeg.deleteFile(file.name);
        }
      }

      return videoUrl;
    } catch (error) {
      // Clean up on error
      try {
        const files = await this.ffmpeg.listDir(".");
        for (const file of files) {
          if (file.name.startsWith("image") || file.name === "output.mp4") {
            await this.ffmpeg.deleteFile(file.name);
          }
        }
      } catch (cleanupError) {
        console.error("Error cleaning up files:", cleanupError);
      }
      throw error;
    }
  }

  cancelRendering() {
    this.cancelRender = true;
  }

  resetCancelFlag() {
    this.cancelRender = false;
  }

  terminate() {
    if (this.ffmpeg) {
      this.ffmpeg.terminate();
    }
  }
}
